import { useEffect, useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2, Globe } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { PageHero, Eyebrow } from "../components/common";
import { BRAND, DESTINATIONS, COURSE_OPTIONS, BUDGET_OPTIONS } from "../mock";
import api from "../lib/api";
import { getUtm } from "../lib/utm";

export default function Contact() {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", course: "", country: "", budget: "", message: "", agent_code: "" });

  useEffect(() => {
    api.get("/public/companies")
      .then(({ data }) => setCompanies(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  const mapSrc = (addr) => `https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast({ title: "Please fill required fields", description: "Name, email and phone are required.", variant: "destructive" });
      return;
    }
    api
      .post("/leads", { ...form, source: "website", ...getUtm() })
      .then(() => {
        setSubmitted(true);
        toast({ title: "Enquiry received! \ud83c\udf93", description: "Our counsellor will reach out within 24 hours." });
      })
      .catch(() => {
        toast({ title: "Something went wrong", description: "Please try again in a moment.", variant: "destructive" });
      });
  };

  const fallback = [{ id: "default", name: BRAND.legal, country_flag: "🇲🇾", address: BRAND.address, phone: BRAND.phone, email: BRAND.email, website: "" }];
  const list = companies.length ? companies : fallback;

  return (
    <>
      <PageHero
        eyebrow="Get In Touch"
        title="Book your free consultation"
        subtitle="Leave with an actual shortlist, not a brochure. Tell us your goals and a dedicated counsellor will build your roadmap."
        image="https://images.unsplash.com/photo-1523240795612-9a054b0db644"
      />
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-5 gap-10">
          {/* Info */}
          <div className="lg:col-span-2 space-y-6">
            <div>
              <Eyebrow>Our Offices</Eyebrow>
              <h2 className="font-display text-3xl font-bold text-[#012a1e] mt-3">We'd love to hear from you</h2>
              <p className="text-slate-500 mt-3">Reach any of our country offices below, or fill the form and we'll call you back.</p>
            </div>
            <div className="space-y-6" data-testid="contact-companies">
              {list.map((c) => (
                <div key={c.id} data-testid={`company-card-${c.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_-20px_rgba(0,64,48,0.35)] overflow-hidden">
                  <div className="p-5">
                    <p className="font-display text-xl font-bold text-[#012a1e]">
                      {c.country_flag} {c.name}
                    </p>
                    <ul className="mt-3 space-y-2.5 text-sm">
                      {c.address && (
                        <li className="flex gap-3 text-slate-600"><MapPin size={17} className="text-[#c9a227] shrink-0 mt-0.5" /> {c.address}</li>
                      )}
                      {c.phone && (
                        <li><a href={`tel:${c.phone}`} className="flex gap-3 items-center text-slate-700 hover:text-[#004030] transition-colors"><Phone size={16} className="text-[#c9a227] shrink-0" /> {c.phone}</a></li>
                      )}
                      {c.email && (
                        <li><a href={`mailto:${c.email}`} className="flex gap-3 items-center text-slate-700 hover:text-[#004030] transition-colors"><Mail size={16} className="text-[#c9a227] shrink-0" /> {c.email}</a></li>
                      )}
                      {c.website && (
                        <li><a href={c.website} target="_blank" rel="noreferrer" className="flex gap-3 items-center text-slate-700 hover:text-[#004030] transition-colors"><Globe size={16} className="text-[#c9a227] shrink-0" /> Visit website</a></li>
                      )}
                    </ul>
                  </div>
                  {c.address && (
                    <iframe
                      title={`Map of ${c.name}`}
                      src={mapSrc(c.address)}
                      className="w-full h-52 border-0 border-t border-slate-100"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      allowFullScreen
                    />
                  )}
                  {(c.offices || []).length > 0 && (
                    <div className="border-t border-slate-100 bg-[#f7faf8] p-5 space-y-4" data-testid={`company-offices-${c.id}`}>
                      <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Branch Offices</p>
                      {c.offices.map((o) => (
                        <div key={o.id} data-testid={`office-${o.id}`} className="rounded-xl bg-white border border-slate-100 overflow-hidden">
                          <div className="p-4">
                            <p className="font-semibold text-[#012a1e]">{o.name}{o.city ? ` · ${o.city}` : ""}</p>
                            {o.address && <p className="flex gap-2 text-sm text-slate-600 mt-1.5"><MapPin size={15} className="text-[#c9a227] shrink-0 mt-0.5" /> {o.address}</p>}
                            {o.phone && <a href={`tel:${o.phone}`} className="flex gap-2 items-center text-sm text-slate-700 hover:text-[#004030] transition-colors mt-1"><Phone size={14} className="text-[#c9a227] shrink-0" /> {o.phone}</a>}
                          </div>
                          {o.address && (
                            <iframe
                              title={`Map of ${o.name}`}
                              src={mapSrc(o.address)}
                              className="w-full h-40 border-0 border-t border-slate-100"
                              loading="lazy"
                              referrerPolicy="no-referrer-when-downgrade"
                              allowFullScreen
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="flex gap-4 bg-[#f7faf8] rounded-xl p-4 border border-slate-100">
                <div className="w-12 h-12 rounded-xl bg-[#004030] grid place-items-center text-[#e0c05a] shrink-0"><Clock size={22} /></div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Office hours</p>
                  <p className="text-[#012a1e] font-medium mt-0.5">Mon – Sat, 9:00 AM – 6:00 PM</p>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            {submitted ? (
              <div className="bg-[#f7faf8] rounded-2xl border border-[#c9a227]/40 p-10 text-center h-full grid place-items-center">
                <div>
                  <div className="w-20 h-20 rounded-full bg-[#004030] grid place-items-center text-[#e0c05a] mx-auto">
                    <CheckCircle2 size={40} />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-[#012a1e] mt-6">Thank you, {form.name.split(" ")[0]}!</h3>
                  <p className="text-slate-500 mt-2 max-w-sm mx-auto">Your enquiry has been received. A dedicated GDIH counsellor will reach out within 24 hours.</p>
                  <Button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", course: "", country: "", budget: "", message: "", agent_code: "" }); }} className="mt-6 bg-[#004030] hover:bg-[#013d2b] text-white rounded-full px-6">Submit another enquiry</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl border border-slate-100 shadow-[0_10px_40px_-20px_rgba(0,64,48,0.25)] p-7 md:p-9 space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" className="h-12 focus-visible:ring-[#004030]" />
                  </div>
                  <div className="space-y-2">
                    <Label>Phone *</Label>
                    <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+60 12-345 6789" className="h-12 focus-visible:ring-[#004030]" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@email.com" className="h-12 focus-visible:ring-[#004030]" />
                </div>
                <div className="grid sm:grid-cols-3 gap-5">
                  <div className="space-y-2">
                    <Label>Course</Label>
                    <Select value={form.course} onValueChange={(v) => set("course", v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{COURSE_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Select value={form.country} onValueChange={(v) => set("country", v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{DESTINATIONS.map((d) => <SelectItem key={d.slug} value={d.name}>{d.flag} {d.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Budget / year</Label>
                    <Select value={form.budget} onValueChange={(v) => set("budget", v)}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>{BUDGET_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <Textarea value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Tell us about your study-abroad goals..." className="min-h-28 focus-visible:ring-[#004030]" />
                </div>
                <div className="space-y-2">
                  <Label>Agent code <span className="text-slate-400 font-normal">(optional — leave blank if applying directly)</span></Label>
                  <Input value={form.agent_code} onChange={(e) => set("agent_code", e.target.value.toUpperCase())} placeholder="e.g. GDIH-MY-12345" className="h-12 focus-visible:ring-[#004030]" data-testid="enquiry-agent-code" />
                </div>
                <Button type="submit" className="w-full bg-[#004030] hover:bg-[#013d2b] text-white rounded-full h-13 py-6 font-semibold text-base">
                  Submit Enquiry <Send size={17} className="ml-1" />
                </Button>
                <p className="text-xs text-center text-slate-400">By submitting you agree to our privacy policy. A counsellor will contact you within 24 hours.</p>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}